import React, {ReactNode, useState} from 'react';
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  DragOverlay,
  UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  AnimateLayoutChanges,
  defaultAnimateLayoutChanges,
} from '@dnd-kit/sortable';
import {Box, Paper, Typography} from '@mui/material';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {ReactComponent as SixDotIcon} from '../../../../assets/images/six-dot-icon.svg';
import {t} from 'i18next';
import {setLstCategorySeq} from '../../../../redux/slice/assignmentSlice';
import {setAvailableCriteria} from '../../../../redux/slice/optimizedSortSlice';

type SortItem = {
  categoryFieldId: number;
  categoryName: string;
  seq: number;
  categoryDirection: boolean;
};

const enrichWithSeq = (
  list: Omit<SortItem, 'seq' | 'categoryDirection'>[]
): SortItem[] =>
  list.map((item, index) => ({
    ...item,
    seq: index + 1,
    categoryDirection: false,
  }));

const i18Path = 'assignment.new.docSequence.optimizedSort';
function SortableItem({id, label}: {id: number; label: string}) {
  const {parentAssignmentGroupID} = useSelector(
    (state: RootState) => state.assignment.formData.assignmentModel
  );
  const treeData = useSelector(
    (state: RootState) => state.assignment.assignmentTree
  );
  const isParentCascaseEnabled = treeData?.find(
    (item) => +item.id === parentAssignmentGroupID
  )?.li_attr.isCascadeEnabled;
  const {attributes, listeners, setNodeRef, isDragging} = useSortable({
    id,
    disabled: isParentCascaseEnabled,
    animateLayoutChanges: defaultAnimateLayoutChanges as AnimateLayoutChanges,
  });

  const style: React.CSSProperties = {
    cursor: isDragging ? 'grabbing' : 'grab',
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Paper
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={style}
      sx={{
        p: 1,
        transition: 'transform 250ms ease, box-shadow 250ms ease',
        backgroundColor: 'transparent',
        boxShadow: 'none',
        borderBottom: 'none',
      }}
    >
      <Box className="draggable-container">
        <SixDotIcon data-testid="six-dot-icon" />
        <Typography
          className="typo-text body3_size"
          data-testid={`optimized-sort-label-${id}`}
        >
          {label}
        </Typography>
      </Box>
    </Paper>
  );
}
function SortableColumn({
  id,
  title,
  items,
  activeId,
}: {
  id: string;
  title: ReactNode;
  items: SortItem[];
  activeId: UniqueIdentifier | null;
}) {
  const {setNodeRef} = useDroppable({id});

  const isDraggingFromElsewhere =
    activeId && !items.find((i) => i.categoryFieldId === Number(activeId));

  const contextItems = isDraggingFromElsewhere
    ? [
        ...items,
        {
          categoryFieldId: Number(activeId),
          categoryName: '',
        },
      ]
    : items;

  return (
    <Box className="box-container" data-testid={`${id}-sortable-column-title`}>
      {title}
      <Box
        ref={setNodeRef}
        sx={{
          minHeight: 200,
        }}
        data-testid={`${id}-sortable-value`}
      >
        <SortableContext
          items={contextItems.map((item) => item.categoryFieldId)}
          strategy={verticalListSortingStrategy}
        >
          {items.length === 0 && id === 'Available Criteria' ? (
            <Box className="available-criteria-container">
              <Typography
                className="typo-text body5_size available-criteria-box"
                data-testid={`optimized-sort-typography-text`}
              >
                {' '}
                {t(`${i18Path}.availableCriteriaContent`)}{' '}
              </Typography>
            </Box>
          ) : (
            items.map((item) => (
              <SortableItem
                key={item.categoryFieldId}
                id={item.categoryFieldId}
                label={item.categoryName}
              />
            ))
          )}
        </SortableContext>
      </Box>
    </Box>
  );
}

const SortManager: React.FC = () => {
  const liveSequence = useSelector(
    (state: RootState) =>
      state.assignment.listData.docPresentationSorting.lstCategorySeq ?? []
  );

  const availableCriteria = useSelector(
    (state: RootState) => state.optimizesSort.items ?? []
  );

  const dispatch = useDispatch();

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);

  const sensors = useSensors(useSensor(PointerSensor));
  const allItems = [...liveSequence, ...availableCriteria];
  const activeItem =
    allItems.find((item) => item.categoryFieldId === Number(activeId)) || null;

  const findList = (id: number) => {
    if (liveSequence.find((item) => item.categoryFieldId === id)) {
      return {
        list: liveSequence,
        update: (newList: SortItem[]) => dispatch(setLstCategorySeq(newList)),
      };
    }
    if (availableCriteria.find((item) => item.categoryFieldId === id)) {
      return {
        list: availableCriteria,
        update: (newList: SortItem[]) =>
          dispatch(setAvailableCriteria(newList)),
      };
    }
    return null;
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const {active, over} = event;
    setActiveId(null);
    if (!over) return;

    const activeListInfo = findList(Number(active.id));
    const overListInfo =
      over.id === 'Live Sequence'
        ? {
            list: liveSequence,
            update: (newList: SortItem[]) =>
              dispatch(setLstCategorySeq(newList)),
          }
        : over.id === 'Available Criteria'
          ? {
              list: availableCriteria,
              update: (newList: SortItem[]) =>
                dispatch(setAvailableCriteria(newList)),
            }
          : findList(Number(over.id));

    if (!activeListInfo || !overListInfo) return;

    const {list: sourceList, update: updateSource} = activeListInfo;
    const {list: destList, update: updateDest} = overListInfo;

    const activeIndex = sourceList.findIndex(
      (item) => item.categoryFieldId === Number(active.id)
    );
    const overIndex = destList.findIndex(
      (item) => item.categoryFieldId === Number(over.id)
    );
    const draggedItem = sourceList[activeIndex];
    if (!draggedItem) return;

    if (sourceList === destList) {
      const updated = [...sourceList];
      updated.splice(activeIndex, 1);
      updated.splice(overIndex, 0, draggedItem);
      const enriched = enrichWithSeq(updated);
      updateSource(enriched);
    } else {
      const newSource = [...sourceList];
      newSource.splice(activeIndex, 1);
      const newDest = [...destList];
      if (overIndex >= 0) {
        newDest.splice(overIndex, 0, draggedItem);
      } else {
        newDest.push(draggedItem);
      }
      const enrichedSource = enrichWithSeq(newSource);
      const enrichedDest = enrichWithSeq(newDest);
      updateSource(enrichedSource);
      updateDest(enrichedDest);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <Box className="category-sequence-container">
        <SortableColumn
          id="Available Criteria"
          title={
            <Typography
              className="typo-text subhead4_size sequence-category-title "
              gutterBottom
              data-testid={`optimized-sort-category-sequence`}
            >
              {' '}
              {t(`${i18Path}.availableCriteria`)}{' '}
            </Typography>
          }
          items={enrichWithSeq(availableCriteria)}
          activeId={activeId}
        />
        <SortableColumn
          id="Live Sequence"
          title={
            <Typography
              className="typo-text subhead4_size sequence-category-title "
              gutterBottom
              data-testid={`optimized-sort-live-sequesnce`}
            >
              {' '}
              {t(`${i18Path}.liveSequence`)}{' '}
            </Typography>
          }
          items={liveSequence}
          activeId={activeId}
        />
      </Box>
      <DragOverlay>
        {activeItem ? (
          <Paper
            sx={{p: 1, boxShadow: 6, backgroundColor: 'transparent'}}
            data-testid="overlay-item"
          >
            <Box className="draggable-container">
              <SixDotIcon />
              <Typography
                className="typo-text body3_size"
                data-testid={`optimized-sort-name-category-active`}
              >
                {activeItem.categoryName}
              </Typography>
            </Box>
          </Paper>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
export default SortManager;
